import { useEffect, useState } from 'react';
import { getXeroAdmin, xeroOAuthStart, xeroDisconnect, xeroConfirmMapping, xeroSuggestMappings, GetXeroAdminOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Link2, Unlink, Users, RefreshCw, Check, Search, Shield, Loader2, ExternalLink } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

type AdminData = GetXeroAdminOutputType;
type Mapping = AdminData['mappings'][0];

function fmtTime(d?: string) { if (!d) return 'Never'; try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return 'Unknown'; } }

export default function XeroPayrollTab() {
  const [data, setData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [search, setSearch] = useState('');
  const [manualId, setManualId] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    getXeroAdmin({}).then(setData).catch(() => toast.error('Failed to load Xero data')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { authUrl } = await xeroOAuthStart({});
      window.open(authUrl, '_blank', 'width=600,height=700');
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try { await xeroDisconnect({}); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed'); }
    setDisconnecting(false);
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    try { const r = await xeroSuggestMappings({}); toast.success(`${r.created} mappings created`); load(); }
    catch { toast.error('Failed'); }
    setSuggesting(false);
  };

  const handleConfirm = async (m: Mapping) => {
    try { await xeroConfirmMapping({ mappingId: m.id, action: 'confirm' }); toast.success('Confirmed'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleManual = async (m: Mapping) => {
    const xeroId = manualId[m.id];
    if (!xeroId) { toast.error('Enter Xero Employee ID'); return; }
    try { await xeroConfirmMapping({ mappingId: m.id, action: 'manual', xeroEmployeeId: xeroId }); toast.success('Mapped'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleUnmap = async (m: Mapping) => {
    try { await xeroConfirmMapping({ mappingId: m.id, action: 'unmap' }); toast.success('Unmapped'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}</div>;
  if (!data) return <p className="text-muted-foreground">Failed to load</p>;

  const isConnected = data.connection?.connectionStatus === 'Connected';
  const filteredMappings = data.mappings.filter(m => !search || m.userName.toLowerCase().includes(search.toLowerCase()) || m.userEmail.toLowerCase().includes(search.toLowerCase()));

  const statusColor = (s: string) => {
    const c: Record<string, string> = { Connected: 'bg-green-500', Disconnected: 'bg-muted-foreground', Revoked: 'bg-destructive', 'Awaiting Approval': 'bg-amber-500' };
    return c[s] || 'bg-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Xero Payroll</h2>
          <p className="text-sm text-muted-foreground">Connection & employee mapping</p>
        </div>
        {data.connection && (
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${statusColor(data.connection.connectionStatus)}`} />
            <span className="text-sm">{data.connection.connectionStatus}</span>
            {data.connection.tenantName && <span className="text-xs text-muted-foreground">· {data.connection.tenantName}</span>}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="py-3">
          <p className="text-xs text-muted-foreground">Status</p>
          <p className="text-sm font-medium">{data.connection?.connectionStatus || 'Not Connected'}</p>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <p className="text-xs text-muted-foreground">Last Sync</p>
          <p className="text-sm font-medium">{fmtTime(data.connection?.lastSuccessfulRead)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <p className="text-xs text-muted-foreground">Mapped Staff</p>
          <p className="text-sm font-medium">{data.stats.mappedCount} of {data.stats.totalStaff}</p>
        </CardContent></Card>
        <Card><CardContent className="py-3">
          <p className="text-xs text-muted-foreground">Sync Failures</p>
          <p className="text-sm font-medium">{data.stats.syncFailureCount}</p>
        </CardContent></Card>
      </div>

      {/* Connect/Disconnect */}
      <div className="flex gap-2">
        {!isConnected ? (
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />}
            Connect Xero
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive"><Unlink className="w-4 h-4 mr-1.5" />Disconnect</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Disconnect Xero?</AlertDialogTitle></AlertDialogHeader>
              <p className="text-sm text-muted-foreground">Staff will lose access to payslips and leave balance data from Xero.</p>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDisconnect} disabled={disconnecting}>Disconnect</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </div>

      <Tabs defaultValue="mappings">
        <TabsList>
          <TabsTrigger value="mappings">Employee Mapping</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="mappings" className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            {isConnected && (
              <Button variant="outline" onClick={handleSuggest} disabled={suggesting}>
                {suggesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Users className="w-4 h-4 mr-1.5" />}
                Auto-Suggest Mappings
              </Button>
            )}
          </div>

          <div className="border border-border rounded-lg divide-y divide-border">
            <div className="hidden sm:grid grid-cols-5 gap-2 p-3 bg-muted/30">
              <span className="text-xs font-semibold text-muted-foreground">Staff Hub</span>
              <span className="text-xs font-semibold text-muted-foreground">Email</span>
              <span className="text-xs font-semibold text-muted-foreground">Xero ID / Match</span>
              <span className="text-xs font-semibold text-muted-foreground">Status</span>
              <span className="text-xs font-semibold text-muted-foreground">Actions</span>
            </div>
            {filteredMappings.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">No mappings yet. Connect Xero and run Auto-Suggest.</p>
            ) : filteredMappings.map(m => (
              <div key={m.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-3 items-center">
                <span className="text-sm font-medium">{m.userName}</span>
                <span className="text-xs text-muted-foreground truncate">{m.userEmail}</span>
                <div className="text-xs text-muted-foreground">
                  {m.xeroEmployeeId ? <span className="font-mono">{m.xeroEmployeeId.slice(0, 8)}...</span> : 'No match'}
                  {m.matchBasis && <span className="ml-1 text-muted-foreground">({m.matchBasis})</span>}
                </div>
                <div>
                  <Badge variant={m.mappingStatus === 'Confirmed' ? 'default' : m.mappingStatus === 'Suggested' ? 'secondary' : m.mappingStatus === 'Conflict' ? 'destructive' : 'outline'}>
                    {m.mappingStatus}
                  </Badge>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {m.mappingStatus === 'Suggested' && (
                    <Button size="sm" variant="outline" onClick={() => handleConfirm(m)}><Check className="w-3 h-3 mr-1" />Confirm</Button>
                  )}
                  {(m.mappingStatus === 'Unmapped' || m.mappingStatus === 'Conflict') && (
                    <div className="flex gap-1">
                      <Input placeholder="Xero ID" value={manualId[m.id] || ''} onChange={e => setManualId(p => ({ ...p, [m.id]: e.target.value }))} className="h-8 w-28 text-xs" />
                      <Button size="sm" variant="outline" onClick={() => handleManual(m)}>Map</Button>
                    </div>
                  )}
                  {m.mappingStatus === 'Confirmed' && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground text-xs" onClick={() => handleUnmap(m)}>Unmap</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          {data.auditLog.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No audit entries yet</p>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border">
              <div className="hidden sm:grid grid-cols-5 gap-2 p-3 bg-muted/30">
                <span className="text-xs font-semibold text-muted-foreground">Action</span>
                <span className="text-xs font-semibold text-muted-foreground">Actor</span>
                <span className="text-xs font-semibold text-muted-foreground">Type</span>
                <span className="text-xs font-semibold text-muted-foreground">Outcome</span>
                <span className="text-xs font-semibold text-muted-foreground">When</span>
              </div>
              {data.auditLog.slice(0, 30).map(a => (
                <div key={a.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-3 items-center">
                  <span className="text-sm truncate">{a.action}</span>
                  <span className="text-xs text-muted-foreground truncate">{a.actorEmail}</span>
                  <span className="text-xs text-muted-foreground">{a.actionType}</span>
                  <Badge variant={a.outcome === 'Success' ? 'default' : a.outcome === 'Denied' ? 'destructive' : 'secondary'}>{a.outcome}</Badge>
                  <span className="text-xs text-muted-foreground">{fmtTime(a.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
